(function () {
  const selector = '.custom-top-bar[data-wvr-top-bar]';
  const focusableSelector = 'a[href], area[href], button, input, select, textarea, iframe, [tabindex]';
  const SWIPE_MIN_DISTANCE = 40;
  const SWIPE_HORIZONTAL_RATIO = 1.5;
  const reducedMotionQuery = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  function prefersReducedMotion() {
    return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
  }

  // Inactive messages must leave the tab order and the accessibility tree,
  // not just the paint. CSS visibility covers the no-JS state; this covers
  // the exiting frame and browsers without inert support.
  function setMessageExposure(message, exposed) {
    if (!message) return;
    if (exposed) {
      message.removeAttribute('aria-hidden');
      if ('inert' in message) message.inert = false;
      else message.removeAttribute('inert');
    } else {
      message.setAttribute('aria-hidden', 'true');
      if ('inert' in message) message.inert = true;
      else message.setAttribute('inert', '');
    }
    message.querySelectorAll(focusableSelector).forEach(function (node) {
      if (exposed) {
        const stored = node.dataset.wvrTopBarTabindex;
        if (typeof stored !== 'string') return;
        if (stored === '') node.removeAttribute('tabindex');
        else node.setAttribute('tabindex', stored);
        delete node.dataset.wvrTopBarTabindex;
        return;
      }
      if (typeof node.dataset.wvrTopBarTabindex !== 'string') {
        node.dataset.wvrTopBarTabindex = node.getAttribute('tabindex') || '';
      }
      node.setAttribute('tabindex', '-1');
    });
  }

  function parseCampaignTime(rawValue) {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;
    const normalized = raw.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/, '$1T$2');
    const parsed = Date.parse(normalized);
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  function init(root) {
    if (!root || root.dataset.wvrTopBarInitialized === 'true') return;
    root.dataset.wvrTopBarInitialized = 'true';

    const wrapper = root.querySelector('.announcement-wrapper');
    if (!wrapper) return;

    const countdown = wrapper.querySelector('[data-wvr-countdown]');
    const delay = Math.max(2, Number(root.dataset.rotationInterval || 4)) * 1000;
    let messages = [];
    let index = 0;
    let rotationInterval = null;
    let countdownInterval = null;
    let hoverPaused = false;
    let focusPaused = false;
    let touchPaused = false;
    let focusOutTimer = null;

    function setCountdownParts(prefixLabel, hours, minutes, seconds, error) {
      if (!countdown) return;
      const prefix = countdown.querySelector('.countdown-inline__prefix');
      if (prefix) prefix.textContent = prefixLabel;
      const values = [hours, minutes, seconds];
      ['hours', 'minutes', 'seconds'].forEach(function (part, partIndex) {
        const node = countdown.querySelector('[data-countdown-' + part + ']');
        if (node) node.textContent = values[partIndex];
      });
      countdown.classList.toggle('countdown-config-error', Boolean(error));
    }

    function updateCountdown() {
      if (!countdown) return false;

      const start = parseCampaignTime(countdown.dataset.start);
      const end = parseCampaignTime(countdown.dataset.end);
      const defaultPrefix = countdown.dataset.defaultPrefix || 'SALE ENDS IN';
      const designMode = Boolean(window.Shopify && window.Shopify.designMode);
      const startIsValid = start === null || Number.isFinite(start);
      const endIsValid = Number.isFinite(end);

      if (!startIsValid || !endIsValid) {
        countdown.hidden = !designMode;
        if (designMode) setCountdownParts('COUNTDOWN: CHECK DATE FORMAT', '--', '--', '--', true);
        return !countdown.hidden;
      }

      if (start !== null && end <= start) {
        countdown.hidden = !designMode;
        if (designMode) setCountdownParts('COUNTDOWN: END MUST BE AFTER START', '--', '--', '--', true);
        return !countdown.hidden;
      }

      const now = Date.now();
      const scheduled = start !== null && now < start;
      const ended = now >= end;

      if (scheduled || ended) {
        countdown.hidden = !designMode;
        if (designMode) {
          setCountdownParts(
            scheduled ? 'COUNTDOWN SCHEDULED' : 'COUNTDOWN ENDED',
            scheduled ? '--' : '00',
            scheduled ? '--' : '00',
            scheduled ? '--' : '00',
            false
          );
        }
        return !countdown.hidden;
      }

      countdown.hidden = false;
      const distance = Math.max(0, end - now);
      const pad = function (value) { return String(value).padStart(2, '0'); };
      setCountdownParts(
        defaultPrefix,
        pad(Math.floor(distance / 3600000)),
        pad(Math.floor((distance % 3600000) / 60000)),
        pad(Math.floor((distance % 60000) / 1000)),
        false
      );
      return true;
    }

    function pushImpression(messageIndex) {
      const message = messages[messageIndex];
      if (!message) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'announcement_bar_impression',
        messageIndex: messageIndex,
        messageText: (message.textContent || '').trim().slice(0, 100)
      });
    }

    function bindClickTracking() {
      messages.forEach(function (message, messageIndex) {
        message.querySelectorAll('a').forEach(function (link) {
          if (link.dataset.wvrTopBarTracked === 'true') return;
          link.dataset.wvrTopBarTracked = 'true';
          link.addEventListener('click', function () {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'announcement_bar_click',
              messageIndex: messageIndex,
              messageText: (message.textContent || '').trim().slice(0, 100),
              linkUrl: link.href
            });
          });
        });
      });
    }

    function stopRotation() {
      if (rotationInterval) window.clearInterval(rotationInterval);
      rotationInterval = null;
    }

    function canRotate() {
      return messages.length > 1 && !hoverPaused && !focusPaused && !touchPaused && !prefersReducedMotion();
    }

    // Single resume path: hover, touch, keyboard focus and the reduced-motion
    // preference each hold the rotation independently.
    function updateRotationState() {
      if (!canRotate()) {
        stopRotation();
        return;
      }
      if (!rotationInterval) rotationInterval = window.setInterval(next, delay);
    }

    function syncExposure() {
      wrapper.querySelectorAll('.announcement-message').forEach(function (message) {
        setMessageExposure(message, message.classList.contains('active'));
      });
    }

    function showMessage(nextIndex) {
      if (messages.length < 2 || nextIndex === index || !messages[nextIndex]) return;
      const current = messages[index];
      const next = messages[nextIndex];
      const focusWasInside = Boolean(current && document.activeElement && current.contains(document.activeElement));
      if (current) {
        current.classList.remove('active');
        current.classList.add('exiting');
        window.setTimeout(function () { current.classList.remove('exiting'); }, 400);
      }
      next.classList.add('active');
      index = nextIndex;
      syncExposure();
      if (focusWasInside) {
        const nextFocusTarget = next.querySelector(focusableSelector);
        if (nextFocusTarget && typeof nextFocusTarget.focus === 'function') nextFocusTarget.focus();
      }
      pushImpression(index);
    }

    function next() {
      if (messages.length > 1) showMessage((index + 1) % messages.length);
    }

    function previous() {
      if (messages.length > 1) showMessage((index - 1 + messages.length) % messages.length);
    }

    function rebuildMessages() {
      stopRotation();
      messages.forEach(function (message) { message.classList.remove('active', 'exiting'); });
      messages = Array.from(wrapper.querySelectorAll('.announcement-message:not([hidden])'));
      index = 0;
      if (messages[0]) {
        messages[0].classList.add('active');
        pushImpression(0);
      }
      syncExposure();
      bindClickTracking();
      updateRotationState();
    }

    const onMouseEnter = function () {
      hoverPaused = true;
      updateRotationState();
    };
    const onMouseLeave = function () {
      hoverPaused = false;
      updateRotationState();
    };
    const onFocusIn = function () {
      focusPaused = true;
      updateRotationState();
    };
    const onFocusOut = function () {
      if (focusOutTimer) window.clearTimeout(focusOutTimer);
      focusOutTimer = window.setTimeout(function () {
        focusOutTimer = null;
        if (wrapper.contains(document.activeElement)) return;
        focusPaused = false;
        updateRotationState();
      }, 0);
    };
    const onMotionPreferenceChange = function () { updateRotationState(); };

    let touchStartX = null;
    let touchStartY = null;
    const resetTouch = function () {
      touchStartX = null;
      touchStartY = null;
      touchPaused = false;
      updateRotationState();
    };
    const onTouchStart = function (event) {
      if (!event.touches || event.touches.length !== 1) {
        touchStartX = null;
        touchStartY = null;
        return;
      }
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
      touchPaused = true;
      updateRotationState();
    };
    // Horizontal-dominant swipes only: an ordinary vertical page-scroll gesture
    // that starts on the bar must never change the message. Listeners stay
    // passive and nothing calls preventDefault, so vertical scrolling is intact.
    const onTouchEnd = function (event) {
      if (touchStartX !== null && touchStartY !== null && event.changedTouches && event.changedTouches.length) {
        const deltaX = event.changedTouches[0].clientX - touchStartX;
        const deltaY = event.changedTouches[0].clientY - touchStartY;
        const isHorizontalSwipe = Math.abs(deltaX) > SWIPE_MIN_DISTANCE
          && Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_HORIZONTAL_RATIO;
        if (isHorizontalSwipe) {
          if (deltaX < 0) next();
          else previous();
        }
      }
      resetTouch();
    };

    wrapper.addEventListener('mouseenter', onMouseEnter);
    wrapper.addEventListener('mouseleave', onMouseLeave);
    wrapper.addEventListener('focusin', onFocusIn);
    wrapper.addEventListener('focusout', onFocusOut);
    wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
    wrapper.addEventListener('touchend', onTouchEnd, { passive: true });
    wrapper.addEventListener('touchcancel', resetTouch, { passive: true });

    if (reducedMotionQuery) {
      if (typeof reducedMotionQuery.addEventListener === 'function') {
        reducedMotionQuery.addEventListener('change', onMotionPreferenceChange);
      } else if (typeof reducedMotionQuery.addListener === 'function') {
        reducedMotionQuery.addListener(onMotionPreferenceChange);
      }
    }

    let countdownWasVisible = updateCountdown();
    rebuildMessages();

    if (countdown) {
      countdownInterval = window.setInterval(function () {
        const countdownIsVisible = updateCountdown();
        if (countdownIsVisible !== countdownWasVisible) {
          countdownWasVisible = countdownIsVisible;
          rebuildMessages();
        }
      }, 1000);
    }

    root._wvrTopBarCleanup = function () {
      stopRotation();
      if (countdownInterval) window.clearInterval(countdownInterval);
      if (focusOutTimer) window.clearTimeout(focusOutTimer);
      focusOutTimer = null;
      wrapper.removeEventListener('mouseenter', onMouseEnter);
      wrapper.removeEventListener('mouseleave', onMouseLeave);
      wrapper.removeEventListener('focusin', onFocusIn);
      wrapper.removeEventListener('focusout', onFocusOut);
      wrapper.removeEventListener('touchstart', onTouchStart);
      wrapper.removeEventListener('touchend', onTouchEnd);
      wrapper.removeEventListener('touchcancel', resetTouch);
      if (reducedMotionQuery) {
        if (typeof reducedMotionQuery.removeEventListener === 'function') {
          reducedMotionQuery.removeEventListener('change', onMotionPreferenceChange);
        } else if (typeof reducedMotionQuery.removeListener === 'function') {
          reducedMotionQuery.removeListener(onMotionPreferenceChange);
        }
      }
    };
  }

  function initAll(scope) {
    const container = scope && scope.querySelectorAll ? scope : document;
    if (container.matches && container.matches(selector)) init(container);
    container.querySelectorAll(selector).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    const root = event.target.matches && event.target.matches(selector)
      ? event.target
      : event.target.querySelector(selector);
    if (root && typeof root._wvrTopBarCleanup === 'function') root._wvrTopBarCleanup();
  });
})();
